'use client'

import { confirm } from "components/Confirmation";
import { alert } from "components/Alert";
import GeneralForm from "components/GeneralForm";
import TitleFormWithEdit from "components/TitleFormWithEdit";
import { addError } from "components/validateGeneralForm";
import { validate } from "components/validateGeneralForm";
import { useEffect, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Container, Table } from "reactstrap";
import contratoService from "services/ContratoService";
import { erroInesperado } from "services/Utils";
import Item from "./Item";
import Loading from "components/Loading";
import ModalHistoricoLogs from "components/ModalHistoricoLogs";

import '../index.scss'

const tipoAditivoList = [
  { value: 'R', label: 'Reajuste' },
  { value: 'M', label: 'Alteração Manual' },
]

const getIdsAlteracoesPersistidasAtivas = (alteracoes = []) => (
  alteracoes
    .filter(el => el.idAditivoItem && el.ativo !== false)
    .map(el => el.id)
)

const normalizeAlteracoes = (alteracoes = []) => (
  alteracoes.map(el => {
    if (el.ativo === false) {
      return {
        ...el,
        novoPrecoUnitario: null,
        novaQuantidade: null
      }
    }

    return el
  })
)

export default function AlterarAditivo() {
  const [data, setData] = useState()
  const [itens, setItens] = useState()
  const [edit, setEdit] = useState(false)
  const [selecionados, setSelecionados] = useState([])
  const [forceReload, setForceReload] = useState(0)
  const [modalLog, setModalLog] = useState({ isOpen: false, tipo: null, idRegistro: null, titulo: '' })
  const params = useParams()
  const navigate = useNavigate()

  const idContrato = parseInt(params.idContrato)
  const idAditivo = parseInt(params.idAditivo)

  const formConfig = [[
    { name: "numero", label: 'Número', type: "text", required: true, colspan: 6, disabled: true },
    { name: "inicioVigencia", label: 'Início da Vigência', type: "date", required: true, colspan: 6 },
    { name: "descricao", label: 'Descrição', type: "textarea", required: true, colspan: 12, style: { height: 130 } },
    { name: "tipo", label: 'Tipo de Aditivo', type: "combo", options: tipoAditivoList, showDefault: true, required: true, colspan: 4, disabled: true }
  ]]

  const carregarAditivo = useCallback(() => {
    setItens()
    contratoService.recuperarAditivo(idContrato, idAditivo).then(res => {
      if (res.success) {
        const alteracoes = normalizeAlteracoes(res.data.alteracoes)
        setData({
          ...res.data,
          alteracoes,
          inicioVigencia: new Date(res.data.inicioVigencia)
        })
        setSelecionados(getIdsAlteracoesPersistidasAtivas(alteracoes))
      }
    })
  }, [idContrato, idAditivo])

  useEffect(() => {
    carregarAditivo()
  }, [carregarAditivo, forceReload])

  useEffect(() => {
    if (data && !itens) {
      const ate = data.vigente ? data.inicioVigencia : null
      contratoService.getItems(idContrato, ate).then(res => {
        if (res.success) {
          setItens(res.data)
        }
      })
    }
  }, [data, itens])

  const handleChange = (name, value) => {
    setData({ ...data, [name]: value })
  }

  const handleChangePrecoUnitario = (id, novoPrecoUnitario) => {
    const _alteracoes = [...data.alteracoes]
    let item = _alteracoes.find(el => el.id == id)
    if (!item) {
      item = { id, novoPrecoUnitario }
      _alteracoes.push(item)
    } else {
      item.novoPrecoUnitario = novoPrecoUnitario
    }
    setData({ ...data, alteracoes: _alteracoes })
  }

  const handleChangeQuantidade = (id, novaQuantidade) => {
    const _alteracoes = [...data.alteracoes]
    let item = _alteracoes.find(el => el.id == id)
    if (!item) {
      item = { id, novaQuantidade }
      _alteracoes.push(item)
    } else {
      item.novaQuantidade = novaQuantidade
    }
    setData({ ...data, alteracoes: _alteracoes })
  }

  const handleChangeSelecao = (id, selecionar) => {
    const alteracaoPersistida = data.alteracoes.find(el => el.id == id && el.idAditivoItem && el.ativo !== false)

    if (!selecionar && alteracaoPersistida) {
      return
    }

    const _selecionados = [...selecionados]
    if (selecionar) {
      if (!_selecionados.includes(id)) {
        _selecionados.push(id)
        setSelecionados(_selecionados)
      }
    } else {
      const alteracao = data.alteracoes.find(el => el.id == id)
      const alteracoes = alteracao?.idAditivoItem && alteracao.ativo === false
        ? data.alteracoes.map(el => el.id == id ? { ...el, novoPrecoUnitario: null, novaQuantidade: null } : el)
        : data.alteracoes.filter(el => el.id != id)
      setData({ ...data, alteracoes })
      setSelecionados(_selecionados.filter(s => s != id))
    }
  }

  const handleClick = (event, source) => {
    if (event === 'log') {
      const isGrupo = !!source.itens || !!source.filhos;
      setModalLog({
        isOpen: true,
        tipo: isGrupo ? 'grupo_item' : (source.idAditivoItem ? 'aditivo_item' : 'item'),
        idRegistro: isGrupo ? source.id : (source.idAditivoItem || source.id),
        titulo: `LOG: ${source.codigo} - ${source.descricao}`
      })
    } else if (event === 'delete') {
      confirm({
        header: 'Excluir item do aditivo',
        text: `Tem certeza que deseja excluir o item "${source.codigo} - ${source.descricao}" deste aditivo?`,
        icon: 'bi-exclamation-triangle',
        onConfirm: async () => {
          try {
            const res = await contratoService.excluirAditivoItem(idContrato, idAditivo, source.id)
            if (res.success) {
              toast('Item excluído do aditivo')
              setForceReload(v => v + 1)
            } else {
              toast.error('Erro ao excluir o item')
            }
          } catch {
            erroInesperado()
          }
        }
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = validate(formConfig, data)

    if (!errors.some(el => el.fieldName == 'inicioVigencia')) {
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)

      if (data.inicioVigencia.getTime() <= hoje) {
        addError(errors, "inicioVigencia", "O início da vigência deve ser posterior a data atual")
      }
    }

    if (errors.length > 0) {
      setData({ ...data, errors })
      return
    }

    const payload = { ...data }
    payload.alteracoes = payload.alteracoes.filter(el => selecionados.includes(el.id) && (!!el.novaQuantidade || !!el.novoPrecoUnitario)) // apenas os selecionados e com valor

    const hasActiveItems = data.alteracoes.some(el => el.ativo !== false)
    if (payload.alteracoes.length === 0 && !hasActiveItems) {
      alert({
        header: 'Atenção',
        text: 'Um aditivo precisa ter ao menos um item ativo.'
      })
      return
    }

    try {
      await contratoService.alterarAditivo(idContrato, idAditivo, payload)
      toast('Registro salvo com sucesso!')
      navigate('/admin/contratos/' + idContrato + '?aba=aditivos')
    } catch (ex) {
      erroInesperado()
    }
  }

  if (!data || !itens)
    return <Loading />

  return (
    <Container fluid className="aditivo-items">
      <div className="mb-2">
        <Button
          type="button"
          color="secondary"
          outline
          onClick={() => navigate(`/admin/contratos/${idContrato}?aba=aditivos`)}
        >
          <i className="bi bi-arrow-left mr-1" />
          Voltar
        </Button>
      </div>
      <TitleFormWithEdit title="Alterar Aditivo" edit={edit} onChangeEdit={setEdit} showButton={!data.vigente} />
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <GeneralForm
              config={formConfig}
              data={data}
              onChange={handleChange}
              disabled={!edit}
            />
            {edit && (
              <div className="text-right mb-2">
                <Button color="primary" type="submit">Salvar</Button>
              </div>
            )}
            <hr />
            <div className="aditivo-table-scroll">
              <Table className="align-items-center table-flush aditivo-wide-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Descrição</th>
                    <th>Unid</th>
                    <th className="text-right">Valor Unit.</th>
                    <th className="text-right">Quantidade</th>
                    <th className="text-right">Valor Total</th>
                    <th className="text-right">Novo Valor Unit.</th>
                    <th className="text-right">Nova Quantidade</th>
                    <th className="text-right">Novo Valor Total</th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map(el => {
                    return (
                      <Item
                        alteracoes={data.alteracoes}
                        selecionados={selecionados}
                        key={el.id}
                        edit={edit}
                        {...el}
                        onChangePrecoUnitario={handleChangePrecoUnitario}
                        onChangeQuantidade={handleChangeQuantidade}
                        onChangeSelecao={handleChangeSelecao}
                        onClick={handleClick}
                      />
                    )
                  })}
                </tbody>
              </Table>
            </div>
            {edit && (
              <div className="text-right mt-2">
                <Button color="primary" type="submit">Salvar</Button>
              </div>
            )}
          </form>

          <ModalHistoricoLogs
            isOpen={modalLog.isOpen}
            toggle={() => setModalLog(p => ({ ...p, isOpen: false }))}
            tipo={modalLog.tipo}
            idRegistro={modalLog.idRegistro}
            titulo={modalLog.titulo}
          />
        </CardBody>
      </Card>
    </Container>
  )
}
